import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import { type UserStore, useUserStore } from "@/Modules/User/Store";
import type { User } from "@/Modules/Models/Domain/User";
import type { ChatUserAddedEventMessageSocket } from "@/Modules/Chat/Models/WebSocket";
import { ChatRoomItemTypeMessageWebSocket } from "@/Modules/Chat/Models/WebSocket";
import { type ChatStore, useChatStore } from "@/Modules/Chat/Store";
import type {
  AllChatRoomItemType,
  CustomChatRoomItem,
  ParticipantRoomItem,
} from "@/Modules/Chat/Models/Domain/Chat";
import { ChatRoomItemType } from "@/Modules/Chat/Models/Domain/Chat";

import { addNewEventWithNeutrals } from "@/Modules/Chat/MessageFilters/helpers/AddNeutralDateEvent";

import { hasValue, isObject } from "@/Utils/Objects";
import { unixSecondsToDate } from "@/Utils/Date";

export interface ExtraParamsChatUserAddedItem {
  chatStore: ChatStore;
  userStore: UserStore;
}

export const isChatUserAddedItemHandler = (message: unknown): message is ChatUserAddedEventMessageSocket => {
  return (
    isObject(message) &&
    hasValue(message.etype) &&
    [ChatRoomItemTypeMessageWebSocket.USER_ADDED, ChatRoomItemTypeMessageWebSocket.USER_LEAVE].includes(
      message.etype as number
    )
  );
};

export const userAddedItemHandler = (
  message: ChatUserAddedEventMessageSocket,
  { chatStore = useChatStore(), userStore = useUserStore() }: ExtraParamsChatUserAddedItem
): Promise<AllChatRoomItemType[]> => {
  const chatUserAddedEvent = transformChatUserAddedEventMessageSocketToCustomChatRoomItem(
    message,
    userStore.user,
    chatStore.rooms[message.chat].participants
  );
  const events = addNewEventWithNeutrals(chatStore.rooms[message.chat].events, chatUserAddedEvent);
  chatStore.rooms[message.chat].events = events;

  return Promise.resolve(events);
};

export const transformChatUserAddedEventMessageSocketToCustomChatRoomItem = (
  chatUserAddedEventMessageSocket: ChatUserAddedEventMessageSocket,
  user: User | null,
  participants: ParticipantRoomItem[]
): CustomChatRoomItem => {
  const isMine = user?.id === chatUserAddedEventMessageSocket.user;
  const username = isMine ? null : findParticipant(chatUserAddedEventMessageSocket.user, participants);
  const type = ChatRoomItemType.NEUTRAL_TEXT;
  const createdAt = unixSecondsToDate(chatUserAddedEventMessageSocket.created);
  const i18nKey = getI18nKey(isMine, chatUserAddedEventMessageSocket.etype);

  return {
    i18nKey,
    params: {
      username,
    },
    id: chatUserAddedEventMessageSocket.eid,
    createdAt,
    type,
    old: !!chatUserAddedEventMessageSocket.old,
    isReceived: false,
  };
};

const getI18nKey = (isMine: boolean, etype: ChatRoomItemTypeMessageWebSocket): string => {
  if (etype === ChatRoomItemTypeMessageWebSocket.USER_ADDED) {
    return isMine ? "chat.message.you_have_joined_the_chat" : "chat.message.user_has_joined_the_chat";
  }

  if (etype === ChatRoomItemTypeMessageWebSocket.USER_LEAVE) {
    return isMine ? "chat.message.you_have_left_the_chat" : "chat.message.user_has_left_the_chat";
  }

  return "";
};

const findParticipant = (userId: string, participants: ParticipantRoomItem[]): string => {
  if (!hasValue(participants) || !Array.isArray(participants)) return "";

  const participant = participants.find((part) => part.user === userId);

  return participant?.name ? participant.name : "";
};

export default {
  isType: isChatUserAddedItemHandler,
  handler: userAddedItemHandler,
} as EventFilter<ExtraParamsChatUserAddedItem>;
